/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2022
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
const BaseClass = require('gemini/dashboard/glass/BaseBoardView');
const CapabilityUtils = require('gemini/app/util/CapabilityUtils');

import StringResources from 'nls/StringResources';
import { createMSTLayoutModel } from 'core/MSTLayoutModelFactory';
import ExploreViewModel from 'layout/explorecontainer/ExploreViewModel';
import { getToolBarItems } from 'layout/explorecontainer/ExploreToolbarExtensions';
import { WidgetHostApp } from 'core/WidgetHostApp';
import CopyPasteHandler from 'util/CopyPasteHandler';
import eventChainLocal from 'gemini/lib/@waca/dashboard-common/dist/utils/EventChainLocal';
import SmartsRecommenderWrapper from 'util/SmartsRecommenderWrapper';
import { hasCapability } from 'util/CapabilityUtil';
import CardNotificationController from '../notification/CardNotificationController';
import { postPasteAction } from 'util/CopyPasteUtils';
import ExploreController from './ExploreController';

import { StartingPointsViewModel } from 'components/startingPoints/StartingPointsViewModel';
import ExplorationAPI from 'api/ExplorationAPI';
import StartingPointsAPI from 'api/StartingPointsAPI';
import CopyPasteAPI from 'api/CopyPasteAPI';

import { destroy, isAlive } from 'mobx-state-tree';
import _ from 'underscore';
import { STARTING_POINTS } from '../../layout/explorecontainer/ExploreViewModel';

const ExploreView = BaseClass.extend({

	init: function(options, ...rest) {
		options.overrideDashboardController = ExploreController;
		ExploreView.inherited('init', this, [
			{
				...options,
				// explore only supports authoring mode, no consume.
				// So we need to force the mode
				isAuthoringMode: true
			},
			...rest
		]);

		this.createLayoutModel = this._createLayoutModel.bind(this);

		this.exploreContainerLayoutModel = null;
	},

	initialize: function() {
		return (ExploreView.inherited('initialize', this, arguments) || Promise.resolve())
			.then(() => {
				this._setupHostApplicationSvc();

				// register our string resource, top priority
				this.stringService.register(StringResources, 1);
			});
	},

	_setupInstrumentation() {
		this.getDashboardApi()
			.getFeature('segment')
			.registerInfoCallback(() => {
				const cards = this.getDashboardApi().getCanvas().findContent({ type: 'exploreCard' });
				return {
					'custom.counts.tabs': cards.length
				};
			});
	},

	getExploreApi: function() {
		if( !this.api ) {
			const startingPointsAPI = new StartingPointsAPI(this.exploreContainerLayoutModel.startingPointsModel);
			this.api = new ExplorationAPI( this.exploreContainerLayoutModel, startingPointsAPI, this._getCopyPasteAPI());
		}
		return this.api;
	},

	_getCopyPasteAPI: function() {
		if (!this.copyPasteAPI) {
			this.copyPasteAPI = new CopyPasteAPI(this.performCopy.bind(this), this.performPaste.bind(this));
		}
		return this.copyPasteAPI;
	},

	getApplicationLabel: function() {
		return StringResources.get('explore');
	},

	getIcon: function() {
		return 'common-explore';
	},

	getIconColor: function() {
		return 'blue';
	},

	getIconTooltip: function() {
		return StringResources.get('explore');
	},

	/**
	 * When doing saveas or save an explore contenet view,
	 * the ID of the content view is updated by the parent
	 * BaseBoardView automatically. Explore relationship model
	 * should keep sync with the content view ID update.
	 *
	 * @override BaseBoardView->setBoardId
	 * @param  {string} id content view ID
	 */
	setBoardId: function(id) {
		ExploreView.inherited('setBoardId', this, arguments);
		if(this.exploreContainerLayoutModel &&
			this.exploreContainerLayoutModel.startingPointsModel &&
			this.exploreContainerLayoutModel.startingPointsModel.relationship){
			this.exploreContainerLayoutModel.startingPointsModel.relationship.setExploreViewId(id);
		}

	},

	/**
	 * NOTE: called from the base class.
	 *
	 * @returns whether the user has the exploration capability or not.
	 */
	hasCapability: function() {
		return hasCapability(this.glassContext);
	},

	/**
	 * NOTE: called from the base class.
	 *
	 * @returns whether the user has the exploration capability on all data sources or not.
	 */
	_checkCapabilityOfSources(spec) {
		if (!this.isAuthoringMode) {
			return Promise.resolve();
		}
		const dashboardApi = this.getDashboardApi();
		const toastMessage = StringResources.get('noCapabilityForOpenExplore');
		return CapabilityUtils.checkAuthoringCapability(dashboardApi, spec, toastMessage)
			.then((hasCapability) => {
				if (!hasCapability) {
					const error = new Error(toastMessage);
					error.code = 'capability';
					throw error;
				}
			});
	},

	/**
	 * called by Glass when opening a new or existing explore which is not open in the background
	 * @returns {Promise}
	 */
	render: function() {
		return ExploreView.inherited('render', this, arguments)
			.then((result) => {
				if(this.exploreContainerLayoutModel.exploreVM){
					const exploreVM = this.exploreContainerLayoutModel.exploreVM;

					exploreVM.setLayoutController(this.boardController.layoutController);

					if (this.exploreContainerLayoutModel.findModel(this.subView)) {
						exploreVM.setDisplayedToCard(this.subView);
					} else if(this.addOnSpec || this.subView === STARTING_POINTS) {
						//When adding a card and opening the exploration, select starting points so the card is added to the end of the list
						exploreVM.setDisplayedToStartingPoints();
					}
				}
				this._setupInstrumentation();
				this.boardController.layoutController.getTopLayoutView().onShow();
				this._mergeAddOnSpec(this);
				return result;
			})
			.catch((err) => {
				this._handleRenderError(err);
			});
	},

	// Called by Glass, do nothing if Explore view is an empty view (case of render returning error)
	setFocus() {
		if (this.el.id !== 'capability__empty__view') {
			return ExploreView.inherited('setFocus', this, arguments);
		}
	},

	// This is a solution to work around the Glass defect GLASS-4904
	// Display empty Explore view in case render has an error and resolve promise so Glass doesn't catch it 
	_handleRenderError(err) {
		if (err.code === 'capability') {
			const navBar = this.glassContext.findElement('com\\.ibm\\.bi\\.glass\\.navbar');
			navBar && navBar.remove();
			const actionBar = this.glassContext.findElement('com\\.ibm\\.bi\\.dashboard\\.toolbars\\.actionBar');
			actionBar && actionBar.remove();
			const dashboardPanel = this.glassContext.findElement('dashboardPanel');
			dashboardPanel && dashboardPanel.remove();
			this.el.innerHTML = '';
			this.el.id = 'capability__empty__view';
			return true;
		}
		throw err;
	},

	/**
	 * called by Glass when switching to an explore was open in the background
	 * @param {Object} - content is passed from Glass that contains the info to create the current app view
	 * @returns {Promise}
	 */
	activate: function(content) {
		if (this.el && this.el.id === 'capability__empty__view') {
			return Promise.resolve(true);
		} else {
			return ExploreView.inherited('activate', this, arguments).then((result) => {
				if( this.exploreContainerLayoutModel.startingPointsModel) {
					const relationshipVM = this.exploreContainerLayoutModel.startingPointsModel.relationship;
					if(relationshipVM){
						relationshipVM.setExploreViewActivated(true);
					}
				}

				this._mergeAddOnSpec(content);
				return result;
			});
		}
	},

	deactivate: function(){
		if (this.el && this.el.id === 'capability__empty__view') {
			return Promise.resolve(true);
		} else {
			return ExploreView.inherited('deactivate', this, arguments).then((result) => {
				if( this.exploreContainerLayoutModel ) {
					if( this.exploreContainerLayoutModel.startingPointsModel) {
						const relationshipVM = this.exploreContainerLayoutModel.startingPointsModel.relationship;
						if(relationshipVM){
							relationshipVM.setExploreViewActivated(false);
						}
					}
				}
				return result;
			});
		}
	},

	remove: function() {
		ExploreView.inherited('remove', this, arguments);
		if( !this._removed ) {
			if( this.exploreContainerLayoutModel ) {
				if( this.exploreContainerLayoutModel.exploreVM ) {
					destroy( this.exploreContainerLayoutModel.exploreVM );
				}
				destroy( this.exploreContainerLayoutModel );
				this.exploreContainerLayoutModel = null;
			}
		}
		this.boardModel = null;
		this._removed = true;

		if( this.api ) {
			this.api.destroy();
			this.api = null;
		}
		if ( this.copyPasteAPI ) {
			this.copyPasteAPI.destroy();
			this.copyPasteAPI = null;
		}
	},

	/**
	 * merge add on spec if available
	 * @param {Object} content - content is passed from Glass (in activate) or from the current view (in render case)
	 * content: { perspective: xx, id: xx, content: xx }
	 */
	_mergeAddOnSpec: function(content){
		if (content) {
			if ((content.addOnSpec && !content.openAppViewCallback) || (!content.addOnSpec && content.openAppViewCallback)) {
				this.logger.error('addOnSpec and openAppViewCallback should coexist');
			} else if (content.addOnSpec && content.openAppViewCallback) {
				content.openAppViewCallback(content.addOnSpec, this);
				delete content.addOnSpec;//Critical always remove addOnSpec
			}
		}
	},

	_createLayoutModel: function(model, env) {
		env.eventRouter = env.eventRouter || this.eventRouter;
		env.createLayoutModel = env.createLayoutModel || this.createLayoutModel;
		env.isFeatureEnabled = env.isFeatureEnabled || this.isFeatureEnabled.bind(this);

		const mstLayoutModel = createMSTLayoutModel(model, env);

		if(model.type === 'exploreContainer') {
			this._initExploreContainerLayoutModel(mstLayoutModel, env);
		}

		return mstLayoutModel;
	},

	/**
	 * Centralized way to return whether a named feature is enabled.
	 * The intent is to hide the underlying technique (currently feature flag but could be dev mode flag)
	 * @param {string} featureName - The feature to check.
	 * @param {string} [enabledValue='true'] - The value to check against to determine enablement is true.
	 * @returns {boolean} - true if the feature is enabled...false otherwise.
	 */
	isFeatureEnabled(featureName, enabledValue = 'true') {
		const dashboardApi = this.getDashboardApi();
		if (dashboardApi) {
			//Access featureChecker to check for new flag for compare table stats
			const featureChecker = dashboardApi.getGlassCoreSvc('.FeatureChecker');
			return (featureChecker && featureChecker.checkValue('explore', featureName, enabledValue));
		}
		return false;
	},

	_initExploreContainerLayoutModel(mstLayoutModel, env) {
		const dashboardApi = this.getDashboardApi();

		this.exploreContainerLayoutModel = mstLayoutModel;
		const { boardModel } = this.exploreContainerLayoutModel;

		const initExploreVM = {
			toolbar: {},
			canvasTabs: {
				tab: 'details'
			},
			notification: {
				active: false
			},
			thumbnails: {
				background: {}
			},
			selectedCards: {
				items: []
			},
			selectionSync: {}
		};

		// Set the initially displayed card.
		const initiallySelectedCard = this._getInitiallySelectedCard(this.exploreContainerLayoutModel);
		if (initiallySelectedCard) {
			initExploreVM.displayedCard = initiallySelectedCard;
			initExploreVM.selectionSync.displayedCard = initiallySelectedCard;
		}

		const exploreVM = ExploreViewModel.create(initExploreVM, {
			...env,
			getToolBarItems: () => getToolBarItems(dashboardApi, { glassContext: this.glassContext }),
			getThumbnailService: () => dashboardApi.getDashboardSvc('.Thumbnail'),
			smartsRecommenderWrapper: new SmartsRecommenderWrapper({
				services: this.services,
				dashboardApi: dashboardApi
			}),
			getNotificationController: () => new CardNotificationController({
				glassContext: this.glassContext
			}),
			getItems: () => this.exploreContainerLayoutModel.items.map(item => item.id)
		});
		if(this.exploreContainerLayoutModel && this.exploreContainerLayoutModel.setExploreVM){
			this.exploreContainerLayoutModel.setExploreVM(exploreVM);
		}
		exploreVM.setLayoutModel(this.exploreContainerLayoutModel);
		exploreVM.setBoardModel(boardModel);

		const startingPointsModel = this._setupStartingPointsModel( {
			exploreVM: exploreVM,
			services: this.services,
			dashboardApi: dashboardApi,
			isFeatureEnabled: env.isFeatureEnabled,
			exploreViewId: this.id,
			isAlive
		} );

		this.exploreContainerLayoutModel.setStartingPointsModel( startingPointsModel );

		// when user switches from 1 datasource to another one on the dataset panel or from datasource grid,
		// dashboard throws these events.
		// We should listen to these events and update activeDataSourceId in Explore view
		this.eventRouter.on('dataSourcePanel:dataSourceSelected', this._onDataSourceChange.bind(this, exploreVM));
		this.eventRouter.on('dataSourceGrid:dataSourceSelected', this._onDataSourceChange.bind(this, exploreVM));
		if(this.exploreContainerLayoutModel && this.exploreContainerLayoutModel.setDashboardApi) {
			this.exploreContainerLayoutModel.setDashboardApi(this.getDashboardApi());
		}
	},

	_setupStartingPointsModel: function( env ) {
		return StartingPointsViewModel.create({
			fields: {},//FieldsModel
			relationship: {
				tooltip: {}
			}
		}, env);

		// env contains:
		// 	- exploreVM => env.exploreViewModel,
		// 	- services => env.services,
		// 	- dashboardApi => env.dashboardApi
	},

	_getInitiallySelectedCard: function(layout) {
		// Choose the first card as the selected card.
		if (layout.items && layout.items.length) {
			const card = layout.items[0];

			return card.id;
		}
	},

	_onDataSourceChange: function(exploreVM, event) {
		if (event.sender) {
			exploreVM.setActiveDataSourceId(event.sender);
		}
	},

	_setupHostApplicationSvc: function() {
		this._hostInstance = new WidgetHostApp( { services: this.services } );
		this._hostInstance.setup();
	},

	preprocessKeyDown: function(event) {
		if ((event.ctrlKey || event.metaKey) && !event.shiftKey) {
			// Don't perform copy and paste when focused on input fields.
			if (event.target.tagName && event.target.tagName.match(/input|textarea|select/igm)) {
				return event;
			}

			let consumeEvent = false;
			switch(event.key) {
				case 'c':
				case 'C':
					if(!this.isTextSelected()) {
						this.performCopy();
						event.preventDefault();
						consumeEvent = true;
					}
					break;
				case 'v':
				case 'V': {
					const skipElements = !!this._hasAncestorClass(event.target, ['slotEditor__slots__slot']);
					if (!skipElements) {
						this.performPaste();

						event.preventDefault();
						consumeEvent = true;
					}
					break;
				}
				default:
					break;
			}

			// When set, prevent dashboard from handling the event. We cannot use preventDefault as we want the default
			// browser copy + paste handlers to execute.
			const eventProps = new eventChainLocal(event);
			eventProps.setProperty('doNotHandle', consumeEvent);
		}

		return event;
	},

	/**
	 * Given an html element, return an array of its classes
	 * @param {Element} element - HTML Element
	 * @returns {Array} - Array of the element's classes
	 */
	_getClasses(element){
		if(!element) {
			return [];
		}
		return element.getAttribute('class') ? element.getAttribute('class').split(' ') : [];
	},

	/**
	 * Given a html element, find a parent node which has one of the given classes
	 * @param {Element} element - HTML Element
	 * @param {Array} classNames - A set of class names
	 * @returns {String} - null if cannot find such a parent; if found, return matched class name
	 */
	_hasAncestorClass(element, classNames) {
		while(element && element !== document) {
			const elementClasses = this._getClasses(element);
			const foundClass = _.find(classNames, className => ~elementClasses.indexOf(className));
			if(foundClass) return foundClass;
			element = element.parentNode;
		}
		return null;
	},

	getExploreVM() {
		return this.exploreContainerLayoutModel && this.exploreContainerLayoutModel.exploreVM;
	},

	getExploreContainerLayoutModel() {
		return this.exploreContainerLayoutModel;
	},

	/**
	 * Copy the currently displayed widget(s) in explore into clipboard
	 * @return {object} a promise which would be resolved when the action is done
	 */
	performCopy() {
		let spec;
		const exploreVM = this.exploreContainerLayoutModel.exploreVM;
		if (!exploreVM.isStartingPointsDisplayed()) {
			// Only copy if not viewing starting points.
			const displayedCardId = exploreVM.getDisplayedCard();
			if (displayedCardId) {
				const exploreCopyPasteHandler = this.getCopyPasteHandler();

				const dashboardApi = this.getDashboardApi();
				const selectedContents = dashboardApi.getFeature('Canvas').getSelectedContentList();
				if (selectedContents.length === 0) {
					return Promise.resolve();
				}

				const selectedWidgets = selectedContents.map(content => {
					return content.getFeature('Serializer').toJSON();
				});
				spec = exploreCopyPasteHandler.doCopyWidgets(displayedCardId, selectedWidgets);
			}
		}
		return Promise.resolve(spec);
	},

	performPaste() {
		return this.getCopyPasteHandler().doPaste()
			.then(result => postPasteAction(this.exploreContainerLayoutModel.exploreVM, result));
	},

	getCopyPasteHandler() {
		return new CopyPasteHandler({
			dashboardApi: this.getDashboardApi(),
			boardModel: this.boardModel,
			exploreContainerLayoutModel: this.exploreContainerLayoutModel,
			glassContext: this.glassContext
		});
	},

	openCardListPane() {
		var plugin = this.glassContext.appController.findPlugin('com.ibm.bi.explore.thumbnailBtn');
		if (plugin && !plugin.isPressed()) {
			plugin.$el.trigger('click');
		}
	},

	addFragment(fragment, options = {}, payloadData = {}) {
		this.exploreContainerLayoutModel.addFragment(fragment, 'createCardFromFragment', payloadData);
		if (options.selectAfterAdd) {
			const addedCardLayoutIds = fragment.model.layout.map(layoutItem => layoutItem.id);

			if (addedCardLayoutIds) {
				this.exploreContainerLayoutModel.exploreVM.setDisplayedToCard(addedCardLayoutIds[addedCardLayoutIds.length - 1]);
			}
		}
	}

});

export default ExploreView;



// WEBPACK FOOTER //
// ./src/js/glass/views/ExploreView.js